import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Minus, Trophy, Users, Share2, Calendar,
  Crown, LogOut, User, Trash2, ChevronRight, Sparkles
} from 'lucide-react';
import { SponsorBanner } from '@/components/SponsorBanner';
import { EventsSubNav } from '@/components/EventsSubNav';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TGA_LOGO = "https://customer-assets.emergentagent.com/job_853d16bb-77c9-4e1d-85ff-db52f6af924e/artifacts/lsv7codb_Copy%20of%20TGA%20pro%20tour%20logo%20transparent.png";
const AMATEUR_LIMIT = 3;

export const PersonalEventsPage = () => {
  const navigate = useNavigate();
  const { member, logout } = useAuth();
  const [hosted, setHosted] = useState([]);
  const [joined, setJoined] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const [form, setForm] = useState({
    name: '',
    description: '',
    num_holes: '3',
    event_date: '',
  });
  const [holes, setHoles] = useState([
    { hole_number: 1, par: 3 },
    { hole_number: 2, par: 3 },
    { hole_number: 3, par: 3 },
  ]);

  const isPro = member?.membership_tier === 'pro' || member?.is_admin;
  const activeHostedCount = hosted.filter(
    (e) => e.status === 'open' || e.status === 'in_progress'
  ).length;
  const atLimit = !isPro && activeHostedCount >= AMATEUR_LIMIT;

  const fetchAll = useCallback(async () => {
    if (!member?.id) return;
    setLoading(true);
    try {
      const [hRes, jRes] = await Promise.all([
        axios.get(`${API_URL}/personal-events/my?host_id=${member.id}`),
        axios.get(`${API_URL}/personal-events/joined?member_id=${member.id}`),
      ]);
      const hostedIds = new Set(hRes.data.map((e) => e.id));
      setHosted(hRes.data);
      setJoined(jRes.data.filter((e) => !hostedIds.has(e.id)));
    } catch {
      toast.error('Failed to load personal events');
    } finally {
      setLoading(false);
    }
  }, [member?.id]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleHolesChange = (numHoles) => {
    const num = parseInt(numHoles);
    setForm({ ...form, num_holes: numHoles });
    const newHoles = [];
    for (let i = 1; i <= num; i++) {
      const existing = holes.find((h) => h.hole_number === i);
      newHoles.push(existing || { hole_number: i, par: 3 });
    }
    setHoles(newHoles);
  };

  const updateHolePar = (holeNumber, delta) => {
    setHoles(holes.map((h) =>
      h.hole_number === holeNumber
        ? { ...h, par: Math.max(1, Math.min(6, h.par + delta)) }
        : h
    ));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Please give your event a name');
      return;
    }
    if (atLimit) {
      toast.error(`Amateur limit reached: ${AMATEUR_LIMIT} active events. Upgrade to Pro for unlimited.`);
      return;
    }
    setCreating(true);
    try {
      const payload = {
        host_id: member.id,
        name: form.name.trim(),
        description: form.description.trim(),
        num_holes: parseInt(form.num_holes),
        holes,
        event_date: form.event_date || null,
      };
      const res = await axios.post(`${API_URL}/personal-events`, payload);
      toast.success('Personal event created');
      setShowCreate(false);
      setForm({ name: '', description: '', num_holes: '3', event_date: '' });
      setHoles([
        { hole_number: 1, par: 3 },
        { hole_number: 2, par: 3 },
        { hole_number: 3, par: 3 },
      ]);
      await fetchAll();
      navigate(`/tournaments/${res.data.id}`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to create event';
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const copyInvite = (inviteCode) => {
    const url = `${window.location.origin}/join/${inviteCode}`;
    navigator.clipboard.writeText(url);
    toast.success('Invite link copied');
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Delete this personal event? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/personal-events/${eventId}?host_id=${member.id}`);
      toast.success('Event deleted');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <SponsorBanner />
      <nav className="border-b border-white/10 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <img
              src={TGA_LOGO}
              alt="TGA Pro Tour"
              className="h-10 object-contain cursor-pointer"
              onClick={() => navigate('/')}
              data-testid="nav-logo"
            />
            <div className="hidden sm:flex items-center gap-1">
              <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-muted-foreground hover:text-white text-sm" data-testid="nav-dashboard">Dashboard</Button>
              <Button variant="ghost" onClick={() => navigate('/schedule')} className="text-white text-sm border-b-2 border-primary rounded-none" data-testid="nav-schedule-active">Schedule</Button>
              <Button variant="ghost" onClick={() => navigate('/tours')} className="text-muted-foreground hover:text-white text-sm">Tours</Button>
              <Button variant="ghost" onClick={() => navigate('/rankings')} className="text-muted-foreground hover:text-white text-sm">Rankings</Button>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/profile')} className="text-muted-foreground hover:text-white" data-testid="nav-profile-btn">
              <User className="mr-2 h-4 w-4" />
              {member?.name}
            </Button>
            <Button variant="ghost" onClick={handleLogout} className="text-muted-foreground hover:text-destructive">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <EventsSubNav />
        {/* Hero header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-secondary/30 bg-secondary/10 text-secondary text-xs font-medium mb-3">
              <Sparkles className="w-3.5 h-3.5" />
              Run your own tournament
            </div>
            <h1 className="text-3xl sm:text-4xl font-heading text-white" data-testid="personal-events-title">
              Personal Events
            </h1>
            <p className="text-muted-foreground mt-2 max-w-2xl text-sm sm:text-base">
              Host private TGA tournaments for friends, family, or your home crew.
              Share an invite link — they don't need an account to join.
              {!isPro && (
                <span className="text-secondary"> Amateurs can host up to {AMATEUR_LIMIT} active events.</span>
              )}
            </p>
          </div>
          <Button
            onClick={() => setShowCreate(true)}
            disabled={atLimit}
            className="bg-secondary text-black hover:bg-secondary/90 disabled:opacity-50"
            data-testid="create-personal-event-btn"
          >
            <Plus className="mr-2 h-4 w-4" />
            New Personal Event
          </Button>
        </div>

        {atLimit && (
          <Card className="bg-amber-500/5 border-amber-500/30 mb-6">
            <CardContent className="p-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Crown className="w-5 h-5 text-amber-400 shrink-0" />
                <p className="text-sm text-amber-100">
                  You've hit the amateur limit of {AMATEUR_LIMIT} active events. Upgrade to Pro for unlimited hosting.
                </p>
              </div>
              <Button onClick={() => navigate('/profile')} size="sm" className="bg-amber-500 text-black hover:bg-amber-400 shrink-0">
                Upgrade
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Hosting */}
        <section className="mb-10">
          <h2 className="text-lg font-heading text-white mb-3 flex items-center gap-2">
            <Crown className="w-5 h-5 text-secondary" />
            Hosted by me
            <span className="text-muted-foreground text-sm font-normal">({hosted.length})</span>
          </h2>
          {loading ? (
            <p className="text-muted-foreground text-sm">Loading...</p>
          ) : hosted.length === 0 ? (
            <Card className="bg-card/30 border-dashed border-white/10">
              <CardContent className="p-8 text-center">
                <Trophy className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
                <p className="text-muted-foreground text-sm">No personal events yet. Create your first one.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {hosted.map((event) => (
                <PersonalEventCard
                  key={event.id}
                  event={event}
                  isHost
                  onCopyInvite={() => copyInvite(event.invite_code)}
                  onOpen={() => navigate(`/tournaments/${event.id}`)}
                  onDelete={() => handleDelete(event.id)}
                />
              ))}
            </div>
          )}
        </section>

        {/* Joined */}
        <section>
          <h2 className="text-lg font-heading text-white mb-3 flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Joined as a player
            <span className="text-muted-foreground text-sm font-normal">({joined.length})</span>
          </h2>
          {!loading && joined.length === 0 ? (
            <Card className="bg-card/30 border-dashed border-white/10">
              <CardContent className="p-8 text-center">
                <Users className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
                <p className="text-muted-foreground text-sm">Friends haven't invited you to any personal events yet.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {joined.map((event) => (
                <PersonalEventCard
                  key={event.id}
                  event={event}
                  onOpen={() => navigate(`/tournaments/${event.id}`)}
                />
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Create modal */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-card border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-white">New Personal Event</DialogTitle>
            <DialogDescription>Private tournament. Invite anyone with a link.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label htmlFor="pe-name" className="text-white">Event name</Label>
              <Input
                id="pe-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Tuesday Night Showdown"
                className="bg-muted border-white/10 text-white"
                data-testid="pe-name-input"
                required
              />
            </div>
            <div>
              <Label htmlFor="pe-desc" className="text-white">Description (optional)</Label>
              <Textarea
                id="pe-desc"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Any details for your players..."
                className="bg-muted border-white/10 text-white"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white">Number of holes</Label>
                <Select value={form.num_holes} onValueChange={handleHolesChange}>
                  <SelectTrigger className="bg-muted border-white/10 text-white" data-testid="pe-num-holes">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3 holes</SelectItem>
                    <SelectItem value="6">6 holes</SelectItem>
                    <SelectItem value="9">9 holes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="pe-date" className="text-white">Event date (optional)</Label>
                <Input
                  id="pe-date"
                  type="datetime-local"
                  value={form.event_date}
                  onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                  className="bg-muted border-white/10 text-white"
                />
              </div>
            </div>
            <div>
              <Label className="text-white mb-2 block">Par per hole</Label>
              <div className="grid grid-cols-3 gap-2">
                {holes.map((hole) => (
                  <div key={hole.hole_number} className="bg-muted rounded-md px-2 py-2 border border-white/5">
                    <div className="text-muted-foreground text-xs text-center mb-1">H{hole.hole_number}</div>
                    <div className="flex items-center justify-center gap-1">
                      <Button type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0 hover:bg-white/5" onClick={() => updateHolePar(hole.hole_number, -1)} data-testid={`pe-par-minus-${hole.hole_number}`}>
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="text-white font-mono w-5 text-center text-sm">{hole.par}</span>
                      <Button type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0 hover:bg-white/5" onClick={() => updateHolePar(hole.hole_number, 1)} data-testid={`pe-par-plus-${hole.hole_number}`}>
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button
                type="submit"
                disabled={creating}
                className="bg-secondary text-black hover:bg-secondary/90"
                data-testid="pe-create-submit"
              >
                {creating ? 'Creating...' : 'Create event'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const PersonalEventCard = ({ event, isHost, onCopyInvite, onOpen, onDelete }) => {
  const statusColor =
    event.status === 'completed'
      ? 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30'
      : event.status === 'in_progress'
        ? 'bg-secondary/15 text-secondary border-secondary/30'
        : 'bg-primary/15 text-primary border-primary/30';

  return (
    <Card
      className="bg-card border-white/10 hover:border-secondary/40 transition-all cursor-pointer group"
      data-testid={`pe-card-${event.id}`}
    >
      <CardHeader className="pb-3" onClick={onOpen}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <CardTitle className="text-white text-base font-medium truncate">{event.name}</CardTitle>
            <CardDescription className="text-xs mt-1">
              {event.num_holes} holes · par {event.total_par}
              {event.event_date && (
                <>
                  {' · '}
                  <Calendar className="inline w-3 h-3 mr-0.5" />
                  {new Date(event.event_date).toLocaleDateString()}
                </>
              )}
            </CardDescription>
          </div>
          <span className={`px-2 py-0.5 text-[10px] rounded-full border ${statusColor} uppercase tracking-wider`}>
            {event.status?.replace('_', ' ')}
          </span>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Users className="w-3.5 h-3.5" />
            {event.participants_count || 0} {event.participants_count === 1 ? 'player' : 'players'}
          </span>
          <div className="flex items-center gap-1">
            {isHost && event.invite_code && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); onCopyInvite(); }}
                className="text-secondary hover:text-secondary hover:bg-secondary/10 h-7 px-2"
                data-testid={`pe-share-${event.id}`}
              >
                <Share2 className="w-3.5 h-3.5 mr-1" />
                <span className="text-xs">Share</span>
              </Button>
            )}
            {isHost && (
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                className="h-7 w-7 text-muted-foreground hover:text-destructive"
                data-testid={`pe-delete-${event.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
            <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-secondary transition-colors" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PersonalEventsPage;
